import { Spinner } from '@consigli/facade';
import { useProject, useProjectId, useUpdateProject } from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import { useCallback } from 'react';
import { HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi';

import { errorToast, successToast } from '@/components/toast';

/**
 * Button to toggle a project's visibility (public/hidden).
 * When a project is hidden, it shows a "Make Public" button.
 * When a project is public, it shows a "Make Hidden" button.
 */
export const ProjectVisibilityButton = () => {
  const projectId = useProjectId();
  const IS_HIDDEN = true;
  const { project } = useProject(projectId, IS_HIDDEN);
  const { updateProject, isLoading: isUpdating } = useUpdateProject();

  const handleToggleVisibility = useCallback(async () => {
    if (!projectId || !project) return;

    const newIsHidden = !project.isHidden;

    try {
      await updateProject({
        projectId,
        data: { isHidden: newIsHidden },
      }).unwrap();
      successToast({
        message: newIsHidden ? 'Project is now hidden' : 'Project is now public',
      });
    } catch (error) {
      console.error('Failed to update project visibility:', error);
      errorToast({ message: 'Failed to update project visibility' });
    }
  }, [projectId, project, updateProject]);

  if (!project) {
    return null;
  }

  return project.isHidden ? (
    <Button variant="default" onClick={handleToggleVisibility} disabled={isUpdating}>
      {isUpdating ? <Spinner size="xsmall" /> : <HiOutlineEye />}
      {isUpdating ? 'Updating...' : 'Make Public'}
    </Button>
  ) : (
    <Button variant="destructive" onClick={handleToggleVisibility} disabled={isUpdating}>
      {isUpdating ? <Spinner size="xsmall" /> : <HiOutlineEyeOff />}
      {isUpdating ? 'Updating...' : 'Make Hidden'}
    </Button>
  );
};
